import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuthState, useAuthRedirect } from '../../hooks/useAuthState';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import { supabase } from '../../lib/supabase';

/**
 * AuthRedirect handles authentication redirects from:
 * 1. OAuth providers (Google)
 * 2. Email confirmation
 * 3. Password reset
 * 4. Magic link login
 * 
 * 
 * 
 user === null means "loading"
user === false means "not authenticated"
user as an object means "authenticated"




 */
export default function AuthRedirect() {
  const { refreshUser, error: authError } = useAuth();
  const {
    isLoading,
    isAuthenticated,
    isUnauthenticated,
    needsEmailConfirmation,
    needsOnboarding,
    needsSubscription,
    canAccessDashboard
  } = useAuthState();
  
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('initializing'); // initializing, processing, redirecting, error
  const [hasProcessed, setHasProcessed] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);

  // Derived state for auth flow status
  const authFlowCompleted = hasProcessed && status !== 'error';

  // Process auth redirect - split into two effects
  // First effect: Handle code exchange
  useEffect(() => {
    const exchangeCode = async () => {
      if (hasProcessed) return;
      
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        
        if (!code) {
          setHasProcessed(true);
          return;
        }

        setStatus('processing');
        // console.log('Processing PKCE auth code');
        
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) throw exchangeError;
        
        if (data?.session) {
          const { success, error: refreshError } = await refreshUser({ force: true });
          if (!success) throw refreshError;
        }

        setHasProcessed(true);
        setStatus('redirecting');
      } catch (error) {
        console.error('Code exchange error:', error);
        setError(error.message);
        setStatus('error');
        navigate('/signin', { 
          replace: true,
          state: { error: error.message }
        });
      }
    };

    exchangeCode();
  }, [navigate, refreshUser]);

  // Second effect: Handle implicit flow and redirects
  useEffect(() => {
    const handleImplicitFlow = async () => {
      if (!authFlowCompleted) return;
      
      try {
        // Handle implicit flow (tokens in hash)
        if (location.hash) {
          // console.log('Processing implicit auth flow');
          const hashParams = new URLSearchParams(location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          
          if (!accessToken || !refreshToken) {
            throw new Error('Missing authentication tokens');
          }

          const { data: { session }, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) throw sessionError;
          
          if (session) {
            const { success, error: refreshError } = await refreshUser({ force: true });
            if (!success) throw refreshError;
          }
        }

        setStatus('redirecting');
      } catch (error) {
        console.error('Auth redirect error:', error);
        setError(error.message);
        setStatus('error');
        navigate('/signin', { 
          replace: true,
          state: { error: error.message }
        });
      }
    };

    handleImplicitFlow();
  }, [location.hash, navigate, refreshUser, authFlowCompleted]);

  // Handle redirects based on user state
  useEffect(() => {
    const handleRedirect = async () => {
      // Don't proceed if we've already redirected
      if (hasRedirected) {
        return;
      }

      // Don't proceed if auth flow isn't complete
      if (!authFlowCompleted) {
        // console.log('Waiting for auth flow to complete');
        return;
      }

      // Don't proceed if auth is still loading
      if (isLoading) {
        // console.log('Waiting for auth to be ready:', {
        //   authFlowCompleted,
        //   isLoading,
        //   isAuthenticated,
        //   status
        // });
        return;
      }

      try {
        // console.log('Handling redirect with state:', {
        //   isAuthenticated,
        //   isUnauthenticated,
        //   needsEmailConfirmation,
        //   needsOnboarding,
        //   needsSubscription,
        //   canAccessDashboard,
        //   status,
        //   authFlowCompleted
        // });

        // Check for password recovery flow
        const urlParams = new URLSearchParams(window.location.search);
        const isPasswordRecovery = urlParams.get('type') === 'recovery';
        const nextUrl = urlParams.get('next');

        // Not authenticated
        if (isUnauthenticated) {
          // console.log('Not authenticated, redirecting to signin');
          setHasRedirected(true);
          navigate('/signin', { 
            replace: true,
            state: { error: error || authError || 'Authentication failed' }
          });
          return;
        }

        // Email confirmation required (unless it's a password recovery flow)
        if (needsEmailConfirmation && !isPasswordRecovery) {
          // console.log('Email not confirmed, redirecting to confirmation page');
          setHasRedirected(true);
          navigate('/email-confirmation', { replace: true });
          return;
        }
        
        // Handle password recovery flow - skip normal flow and go directly to account
        if (isPasswordRecovery && isAuthenticated) {
          // console.log('Password recovery flow detected, redirecting to account security tab');
          // console.log('Current location:', location.pathname + location.search);
          // Use the next URL if provided, otherwise default to account security tab
          const destination = nextUrl || '/account?tab=security';
          // console.log('Navigating to:', destination);
          setHasRedirected(true); // Prevent further redirects
          navigate(destination, { replace: true });
          return;
        }
        
        // Onboarding required
        if (needsOnboarding) {
          // console.log('Onboarding not completed, redirecting to onboarding');
          setHasRedirected(true);
          navigate('/onboarding', { replace: true });
          return;
        }
        
        // Subscription required
        if (needsSubscription) {
          // console.log('No active subscription, redirecting to payment');
          setHasRedirected(true);
          navigate('/payment', { replace: true });
          return;
        }
        
        // All requirements met
        if (canAccessDashboard) {
          // console.log('All requirements met, redirecting to dashboard');
          setHasRedirected(true);
          navigate('/dashboard', { replace: true });
          return;
        }

      } catch (redirectError) {
        console.error('Redirect error:', redirectError);
        setError(redirectError.message);
        navigate('/signin', { 
          replace: true,
          state: { error: redirectError.message }
        });
      }
    };

    handleRedirect();
  }, [
    hasRedirected,
    isLoading,
    isAuthenticated,
    isUnauthenticated,
    needsEmailConfirmation,
    needsOnboarding,
    needsSubscription,
    canAccessDashboard,
    navigate, 
    error,
    authError,
    authFlowCompleted,
    status
  ]);

  // Fallback redirect if no auth tokens found
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isUnauthenticated && status === 'initializing') {
        // console.warn('No auth token found after timeout, redirecting to sign in');
        navigate('/signin', { replace: true });
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, [isUnauthenticated, status, navigate]);

  // Render error state
  if (error) {
    return (
      <ErrorDisplay
        fullScreen
        error={error}
        title="Authentication Error"
        onRetry={() => navigate('/signin')}
        retryText="Return to Sign In"
      />
    );
  }

  const message = {
    'initializing': 'Initializing...',
    'processing': 'Processing authentication...',
    'redirecting': 'Redirecting...',
    'error': 'Authentication failed'
  }[status] || 'Processing...';

  return <LoadingSpinner fullScreen message={message} size="lg" />;
} 